import { FunctionComponent } from 'react'
import { Slide } from '@widgets/Gallery/types'
import styled, { css } from 'styled-components'
import Caption from './Caption'
import Counter from './Counter'
import { mobileCSS, tabletCSS, desktopCSS } from '@measures/responsive'
import { useFullScreenGallery } from '@hooks/useFullScreenGallery'
import useIsInScribbleContent from '@hooks/useIsInScribbleContent'

const SlideInfoContainer = styled.div<{
  isFullScreen: boolean
  expandedCaption: boolean
}>`
  ${({
    theme: {
      colors: { white },
    },
    expandedCaption,
    isFullScreen,
  }) => {
    /**
     * $caption-block-font-size *
     * $caption-block-line-height * $caption-max-lines + $red-corner-offset;
     */
    const fullscreenInfoMaxHeightDesktop = 15 * 1.5 * 3 + 20
    /**
     * $caption-block-font-size *
     * $caption-block-line-height * $caption-max-lines + rem(16px);
     */
    const inlineInfoMaxHeightMobile = 15 * 1.5 * 2 + 16

    return css`
      position: relative;
      background-color: ${white};
      box-sizing: border-box;

      ${desktopCSS(css`
        display: flex;
        justify-content: flex-start;
        margin-bottom: 0;
        ${!expandedCaption &&
        css`
          min-height: ${fullscreenInfoMaxHeightDesktop}px;
        `}
      `)}

      ${tabletCSS(css`
        display: flex;
        justify-content: flex-start;
        margin-bottom: 0;
        ${!expandedCaption &&
        css`
          min-height: ${fullscreenInfoMaxHeightDesktop}px;
        `}
      `)}

    ${mobileCSS(css`
        padding: 0;
        height: ${inlineInfoMaxHeightMobile}px;
        ${expandedCaption &&
        css`
          height: auto;
        `}
        ${isFullScreen &&
        css`
          height: auto;
          position: absolute;
          left: 0;
          bottom: 0;
          right: 0;

          background-color: transparent;
          display: flex;
          flex-direction: column;
          padding: 12px;

          &:before {
            content: '';
            position: absolute;
            left: 0;
            bottom: 0;
            width: 100%;
            height: 100%;

            @include linear-gradient(
              to top,
              rgba(0, 0, 0, 1),
              rgba(0, 0, 0, 0),
              'background'
            );
          }
        `}
      `)}
    `
  }}
`

type SwiperSlideItemProps = Pick<Slide, 'caption'> & {
  counterLabel?: string
  isSingleSlide?: boolean
}

const SlideFooter: FunctionComponent<SwiperSlideItemProps> = ({
  caption,
  counterLabel,
  isSingleSlide,
}) => {
  const { isFullScreen, hasControls } = useFullScreenGallery()
  const isInScribble = useIsInScribbleContent()
  const toggledOffOnClick = isFullScreen && !hasControls
  const hasElements = !!caption || !!counterLabel
  const expandedCaption = !!isSingleSlide && !isInScribble

  if (toggledOffOnClick || !hasElements) {
    return null
  }

  return (
    <SlideInfoContainer
      expandedCaption={expandedCaption}
      isFullScreen={isFullScreen}>
      {!!counterLabel && <Counter>{counterLabel}</Counter>}
      {!!caption && <Caption isSingleSlide={isSingleSlide}>{caption}</Caption>}
    </SlideInfoContainer>
  )
}

export default SlideFooter
